import type { FieldValue } from "@/components/shared/UniversalCRMTable/types";
import type { SimpleFieldConfig } from "@/components/shared/UniversalCRMTable/types";
import { validateContactField } from "./contact-field-validator";

/**
 * Inline edit validation result
 */
export interface InlineEditValidation {
  isValid: boolean;
  errors: string[];
  sanitizedValue?: FieldValue;
}

/**
 * Validate a single field change for inline editing
 */
export function validateInlineEdit(
  fieldId: string,
  newValue: FieldValue,
  fieldConfig: SimpleFieldConfig
): InlineEditValidation {
  // Validate field exists and is editable
  if (!fieldConfig.editable) {
    return {
      isValid: false,
      errors: [`Field "${fieldId}" is not editable`],
    };
  }

  // Validate field type
  const fieldType = fieldConfig.type;
  if (!fieldType) {
    return {
      isValid: false,
      errors: [`Field "${fieldId}" has no type defined`],
    };
  }

  // Type-specific validation
  const typeValidation = validateFieldType(newValue, fieldType, fieldId);
  if (!typeValidation.isValid) {
    return typeValidation;
  }

  // Field-specific validation using contact field validator
  const fieldValidation = validateContactField(fieldId, typeValidation.sanitizedValue);
  if (!fieldValidation.isValid) {
    return {
      isValid: false,
      errors: fieldValidation.errors,
    };
  }

  return {
    isValid: true,
    errors: [],
    sanitizedValue: fieldValidation.sanitizedValue as FieldValue,
  };
}

/**
 * Validate value matches field type
 */
function validateFieldType(
  value: FieldValue,
  fieldType: string,
  fieldId: string
): InlineEditValidation {
  // Allow null/undefined for optional fields
  if (value === null || value === undefined) {
    return { isValid: true, errors: [], sanitizedValue: null };
  }

  switch (fieldType) {
    case "text":
    case "textarea":
    case "select":
    case "user":
    case "client-select":
    case "company-select":
    case "ticket-select":
    case "template-select":
      return validateStringType(value, fieldId);

    case "number":
      return validateNumberType(value, fieldId);

    case "boolean":
      return validateBooleanType(value, fieldId);

    case "date":
    case "datetime":
    case "time":
    case "due-date":
    case "date-worked":
      return validateDateType(value, fieldId);

    case "multi-user":
    case "tags":
      return validateArrayType(value, fieldId);

    case "array":
      return validateArrayType(value, fieldId);

    case "json":
      return validateJsonType(value, fieldId);

    case "custom":
      // Custom types pass through
      return { isValid: true, errors: [], sanitizedValue: value };

    default:
      return { isValid: true, errors: [], sanitizedValue: value };
  }
}

/**
 * Validate string type
 */
function validateStringType(value: FieldValue, fieldId: string): InlineEditValidation {
  if (typeof value === "string") {
    return { isValid: true, errors: [], sanitizedValue: value.trim() };
  }

  if (typeof value === "number") {
    return { isValid: true, errors: [], sanitizedValue: String(value) };
  }

  return {
    isValid: false,
    errors: [`Field "${fieldId}" must be a string, got ${typeof value}`],
  };
}

/**
 * Validate number type
 */
function validateNumberType(value: FieldValue, fieldId: string): InlineEditValidation {
  if (typeof value === "number") {
    if (isNaN(value)) {
      return {
        isValid: false,
        errors: [`Field "${fieldId}" must be a valid number`],
      };
    }
    return { isValid: true, errors: [], sanitizedValue: value };
  }

  if (typeof value === "string") {
    const parsed = parseFloat(value);
    if (isNaN(parsed)) {
      return {
        isValid: false,
        errors: [`Field "${fieldId}" must be a valid number`],
      };
    }
    return { isValid: true, errors: [], sanitizedValue: parsed };
  }

  return {
    isValid: false,
    errors: [`Field "${fieldId}" must be a number, got ${typeof value}`],
  };
}

/**
 * Validate boolean type
 */
function validateBooleanType(value: FieldValue, fieldId: string): InlineEditValidation {
  if (typeof value === "boolean") {
    return { isValid: true, errors: [], sanitizedValue: value };
  }

  if (typeof value === "string") {
    const lower = value.toLowerCase();
    if (lower === "true" || lower === "1" || lower === "yes") {
      return { isValid: true, errors: [], sanitizedValue: true };
    }
    if (lower === "false" || lower === "0" || lower === "no") {
      return { isValid: true, errors: [], sanitizedValue: false };
    }
  }

  return {
    isValid: false,
    errors: [`Field "${fieldId}" must be a boolean`],
  };
}

/**
 * Validate date type
 */
function validateDateType(value: FieldValue, fieldId: string): InlineEditValidation {
  if (typeof value === "string") {
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return {
          isValid: false,
          errors: [`Field "${fieldId}" must be a valid date`],
        };
      }
      return { isValid: true, errors: [], sanitizedValue: date.toISOString() };
    } catch (error) {
      return {
        isValid: false,
        errors: [`Field "${fieldId}" must be a valid date`],
      };
    }
  }

  if (value instanceof Date) {
    return { isValid: true, errors: [], sanitizedValue: value.toISOString() };
  }

  return {
    isValid: false,
    errors: [`Field "${fieldId}" must be a date string or Date object`],
  };
}

/**
 * Validate array type
 */
function validateArrayType(value: FieldValue, fieldId: string): InlineEditValidation {
  if (!Array.isArray(value)) {
    return {
      isValid: false,
      errors: [`Field "${fieldId}" must be an array`],
    };
  }

  // Validate all items are strings or numbers
  const errors: string[] = [];
  for (let i = 0; i < value.length; i++) {
    const item = value[i];
    if (item !== null && item !== undefined) {
      if (typeof item !== "string" && typeof item !== "number") {
        errors.push(
          `Field "${fieldId}" array item at index ${i} must be string or number, got ${typeof item}`
        );
      }
    }
  }

  if (errors.length > 0) {
    return { isValid: false, errors };
  }

  return { isValid: true, errors: [], sanitizedValue: value };
}

/**
 * Validate JSON type
 */
function validateJsonType(value: FieldValue, fieldId: string): InlineEditValidation {
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    try {
      JSON.stringify(value);
      return { isValid: true, errors: [], sanitizedValue: value };
    } catch (error) {
      return {
        isValid: false,
        errors: [`Field "${fieldId}" must contain JSON-serializable values`],
      };
    }
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return { isValid: true, errors: [], sanitizedValue: parsed };
    } catch (error) {
      return {
        isValid: false,
        errors: [`Field "${fieldId}" must be valid JSON`],
      };
    }
  }

  return {
    isValid: false,
    errors: [`Field "${fieldId}" must be a JSON object or string`],
  };
}
